import React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import type { Editor as TinyMCEEditor, EditorOptions } from 'tinymce';
// TinyMCE core and assets imported once so the bundle is evaluated a single time
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom/model';

// TinyMCE plugins used by the editor
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/codesample';
import 'tinymce/plugins/quickbars';

// TinyMCE styles (UI + content)
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';
import 'tinymce/skins/content/default/content.min.css';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  className?: string;
  toolbar?: string;
  plugins?: string[];
  menubar?: boolean;
  statusbar?: boolean;
  branding?: boolean;
  elementpath?: boolean;
  resize?: boolean | 'both' | 'vertical';
  content_style?: string;
}

const defaultPlugins = [
  'advlist',
  'autolink',
  'lists',
  'link',
  'image',
  'charmap',
  'preview',
  'anchor',
  'searchreplace',
  'visualblocks',
  'code',
  'fullscreen',
  'insertdatetime',
  'media',
  'table',
  'help',
  'wordcount',
  'emoticons',
  'codesample',
  'quickbars',
];

const defaultToolbar =
  'undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | ' +
  'bullist numlist outdent indent | removeformat | link image media table | ' +
  'forecolor backcolor | emoticons charmap | preview code fullscreen | help';

const defaultContentStyle = `
  body { 
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
    font-size: 14px; 
    line-height: 1.6;
    color: hsl(var(--foreground));
    margin: 1rem;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 0.375rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }
  blockquote {
    border-left: 4px solid hsl(var(--border));
    margin: 1.5rem 0;
    padding-left: 1rem;
    font-style: italic;
    color: hsl(var(--muted-foreground));
  }
  pre {
    background-color: hsl(var(--muted));
    border-radius: 0.375rem;
    padding: 1rem;
    overflow-x: auto;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
  }
  table th, table td {
    border: 1px solid hsl(var(--border));
    padding: 0.75rem;
    text-align: left;
  }
  table th {
    background-color: hsl(var(--muted));
    font-weight: 600;
  }
  a {
    color: hsl(var(--primary));
    text-decoration: underline;
  }
  a:hover {
    color: hsl(var(--primary));
  }
`;

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder = 'Start typing...',
  height = 400,
  disabled = false,
  className,
  toolbar = defaultToolbar,
  plugins = defaultPlugins,
  menubar = false,
  statusbar = true,
  branding = false,
  elementpath = false,
  resize = 'vertical',
  content_style = defaultContentStyle,
}) => {
  // type EditorLike = TinyMCEEditor; // unused

  const handleEditorChange = (content: string) => {
    if (onChange) {
      onChange(content);
    }
  };

  const normalizedResize: boolean =
    resize === 'vertical' ? true : Boolean(resize);
  const editorInit: Partial<EditorOptions> & {
    setup?: (editor: TinyMCEEditor) => void;
  } = {
    height,
    menubar,
    plugins,
    toolbar,
    statusbar,
    branding,
    elementpath,
    resize: normalizedResize,
    content_style,
    placeholder,

    // Content filtering and cleanup
    paste_data_images: true,
    paste_as_text: false,
    paste_retain_style_properties:
      'color font-size font-family background-color',
    paste_remove_styles_if_webkit: false,

    // Image handling
    automatic_uploads: true,
    file_picker_types: 'image',
    images_upload_handler: (blobInfo: {
      blob: () => Blob;
    }): Promise<string> => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(blobInfo.blob());
      });
    },

    // Link handling
    link_default_target: '_blank',
    link_assume_external_targets: true,

    // Table settings
    table_default_attributes: {
      border: '1',
    },
    table_default_styles: {
      'border-collapse': 'collapse',
    },

    // Advanced settings
    contextmenu: ['link', 'image', 'table'],
    quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
    quickbars_insert_toolbar: 'quickimage quicktable',

    // Mobile responsive
    mobile: {
      theme: 'silver',
      plugins: ['autosave', 'lists', 'autolink'] as any,
      toolbar: 'undo redo | bold italic | bullist numlist | link',
    },

    // Accessibility
    a11y_advanced_options: true,

    // Custom styles for better integration
    skin: 'oxide',

    // Performance
    browser_spellcheck: true,
    contextmenu_never_use_native: true,

    setup: (editor: any) => {
      editor.on('init', () => {
        if (onFocus) {
          editor.on('focus', onFocus);
        }
        if (onBlur) {
          editor.on('blur', onBlur);
        }
      });

      // Custom button for asset library (placeholder)
      editor.ui.registry.addButton('assetlibrary', {
        text: 'Assets',
        tooltip: 'Insert from Asset Library',
        onAction: () => {
          // TODO: Open asset library modal
          console.log('Asset library would open here');
        },
      });

      // Add custom format for callout boxes
      editor.formatter.register('callout', {
        block: 'div',
        classes: 'callout',
        wrapper: true,
      });

      editor.ui.registry.addToggleButton('callout', {
        text: 'Callout',
        tooltip: 'Create callout box',
        onAction: () => {
          editor.formatter.toggle('callout');
        },
        onSetup: (api: any) => {
          const nodeChangeHandler = () => {
            api.setActive(editor.formatter.match('callout'));
          };
          editor.on('NodeChange', nodeChangeHandler);
          return () => editor.off('NodeChange', nodeChangeHandler);
        },
      });
    },
  };

  return (
    <div className={cn('w-full', className)}>
      <Editor
        value={value}
        onEditorChange={handleEditorChange}
        disabled={disabled}
        init={editorInit as any}
        apiKey='no-api-key' // For self-hosted version
      />

      {/* Custom styles for callout boxes */}
      <style>{`
        .tox .tox-edit-area {
          border-radius: 0.375rem;
        }
        .callout {
          background-color: hsl(var(--primary) / 0.12);
          border-left: 4px solid hsl(var(--primary));
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.375rem;
        }
        .callout p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

